import { useStore } from './store';

export interface NodeGraphEvent {
  type: string;
  nodes?: unknown;
  connections?: unknown;
  ts?: number;
  payload?: any;
}

interface Subscriber {
  onEvent: (event: NodeGraphEvent) => void;
  onConnection?: (connected: boolean) => void;
}

// One transport per document, not per component. WebSockets do not occupy
// Chromium's six HTTP/1.1 request slots like permanent SSE/fetch streams do.
const subscribers = new Set<Subscriber>();
let socket: WebSocket | undefined;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let connectionDeadline: ReturnType<typeof setTimeout> | undefined;
let retryDelay = 250;
let connected = false;
let suspended = false;
let latestGraph: NodeGraphEvent | undefined;

function invoke<T>(callback: ((value: T) => void) | undefined, value: T) {
  try { callback?.(value); }
  catch { /* One component must not break delivery to the others. */ }
}

function notifyConnection(next: boolean) {
  if (connected === next) return;
  connected = next;
  for (const subscriber of [...subscribers]) invoke(subscriber.onConnection, next);
}

function closeSocket() {
  clearTimeout(connectionDeadline);
  connectionDeadline = undefined;
  const previous = socket;
  socket = undefined;
  if (previous) {
    previous.onopen = previous.onmessage = previous.onerror = previous.onclose = null;
    if (previous.readyState === WebSocket.CONNECTING || previous.readyState === WebSocket.OPEN) previous.close();
  }
  notifyConnection(false);
}

function stopTransport() {
  clearTimeout(retryTimer);
  retryTimer = undefined;
  closeSocket();
}

function retry() {
  closeSocket();
  if (!subscribers.size || suspended || retryTimer !== undefined) return;
  retryTimer = setTimeout(() => {
    retryTimer = undefined;
    connect();
  }, retryDelay);
  retryDelay = Math.min(retryDelay * 2, 5000);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function connect() {
  if (!subscribers.size || suspended || socket || retryTimer !== undefined) return;
  const { protocol, host, hostname } = window.location;
  const loopback = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
  if (protocol !== 'https:' && !(protocol === 'http:' && loopback)) return;

  let current: WebSocket;
  try { current = new WebSocket(`${protocol === 'https:' ? 'wss:' : 'ws:'}//${host}/api/node-graph/ws`); }
  catch { retry(); return; }
  socket = current;
  connectionDeadline = setTimeout(() => { if (socket === current) retry(); }, 5000);

  current.onopen = () => {
    if (socket !== current) return;
    try {
      // Read at open, not mount: reconnect must use the current Studio token.
      // No URL/subprotocol credential and no cookie/storage access or writes.
      const token = useStore.getState().token;
      if (token) current.send(JSON.stringify({ type: 'authenticate', token }));
      // Otherwise the server may already have authenticated the HttpOnly cookie.
    } catch { retry(); }
  };
  current.onmessage = ({ data }) => {
    if (socket !== current || typeof data !== 'string') return;
    let parsed: unknown;
    try { parsed = JSON.parse(data); }
    catch { return; }
    if (!isRecord(parsed) || typeof parsed.type !== 'string') return;
    const event = parsed as unknown as NodeGraphEvent;
    if (event.type === 'snapshot' || event.type === 'update') {
      if (!isRecord(event.nodes) || !isRecord(event.connections) || typeof event.ts !== 'number' || !Number.isFinite(event.ts)) return;
      clearTimeout(connectionDeadline);
      connectionDeadline = undefined;
      latestGraph = event;
      retryDelay = 250;
      // The first graph message is proof of server-side authentication, not
      // merely an open TCP/WebSocket handshake.
      notifyConnection(true);
    } else if (!connected) return;
    for (const subscriber of [...subscribers]) invoke(subscriber.onEvent, event);
  };
  current.onerror = current.onclose = () => { if (socket === current) retry(); };
}

function pagehide() {
  suspended = true;
  stopTransport();
  // Keep subscribers for BFCache restores; never replay custom job events.
}

function pageshow() {
  suspended = false;
  connect();
}

/** Subscribe to live events; late subscribers receive only the latest graph. */
export function subscribeNodeGraphEvents(
  onEvent: Subscriber['onEvent'],
  onConnection?: Subscriber['onConnection'],
): () => void {
  const subscriber = { onEvent, onConnection };
  subscribers.add(subscriber);
  if (subscribers.size === 1) {
    window.addEventListener('pagehide', pagehide);
    window.addEventListener('pageshow', pageshow);
  }
  invoke(onConnection, connected);
  if (latestGraph) invoke(onEvent, latestGraph);
  connect();
  return () => {
    if (!subscribers.delete(subscriber) || subscribers.size) return;
    stopTransport();
    latestGraph = undefined;
    retryDelay = 250;
    suspended = false;
    window.removeEventListener('pagehide', pagehide);
    window.removeEventListener('pageshow', pageshow);
  };
}